import { zodResolver } from '@hookform/resolvers/zod';
import { Clock, Dot, Edit, Loader2, Users } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';

import { useGetCostumeSizings } from '~entities/recital-manager/api/get-costume-sizings';
import { useUpdatePerformanceMutation } from '~entities/recital-manager/api/update-performance';
import { useUpdateStudentCostumeSizingMutation } from '~entities/recital-manager/api/update-student-costume-sizing';
import type { Performance } from '~entities/recital-manager/model/recital';
import { useStudentsByIds } from '~entities/student';

import { Button } from '~shared/shadcn/ui/button';
import { Form } from '~shared/shadcn/ui/form';
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '~shared/shadcn/ui/sheet';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '~shared/shadcn/ui/tabs';

import { BasicInfoTab } from './basic-info-tab';
import { CostumeDetailsTab } from './costume-details-tab';

// Define the form schema here and export it
const performanceFormSchema = z.object({
  name: z.string().min(1, 'Performance name is required'),
  duration: z.coerce.number().min(1, 'Duration must be at least 1 minute'),
  notes: z.string().optional(),
  costumeName: z.string().optional(),
  costumeDescription: z.string().optional(),
  costumeCompany: z.string().optional(), // Now used as supplier
  costumeStyleNumber: z.string().optional(),
  costumeColor: z.string().optional(),
  // Sizing fields
  tightsSelection: z.string().optional(),
  shoeType: z.string().optional(),
  hairAndMakeupNotes: z.string().optional(),
  // Extra fields
  propsNeeded: z.boolean().optional().default(false),
  propsDescription: z.string().optional(),
  // Bulk fill fields
  bulkCostumeSize: z.string().optional(),
  bulkTopSize: z.string().optional(),
  bulkBottomSize: z.string().optional(),
  bulkTightsSize: z.string().optional(),
  bulkShoeSize: z.string().optional(),
  // Individual student sizes - will be populated dynamically from studentIds
  studentSizes: z
    .array(
      z.object({
        studentId: z.string(),
        studentName: z.string(),
        costumeSize: z.string().optional(),
        topSize: z.string().optional(),
        bottomSize: z.string().optional(),
        tightsSize: z.string().optional(),
        shoeSize: z.string().optional(),
        notes: z.string().optional(),
      }),
    )
    .optional(),
  studentIds: z
    .array(
      z.object({
        label: z.string(),
        value: z.string(),
      }),
    )
    .optional(),
});

export type PerformanceFormValues = z.infer<typeof performanceFormSchema>;

interface EditPerformanceSheetProps {
  actId: string;
  performance: Performance;
  children?: React.ReactNode;
}

export const EditPerformanceSheet = ({
  actId,
  performance,
  children,
}: EditPerformanceSheetProps) => {
  const [open, setOpen] = useState(false);

  // Fetch student names by their IDs
  const { studentsMap, isLoading: isLoadingStudents } = useStudentsByIds(performance.studentIds || []);

  // Fetch sizing data for this specific performance
  const { data: allSizings = [], isLoading: isLoadingSizings } = useGetCostumeSizings(
    performance.recitalId,
    performance._id // Filter by performanceId
  );

  const form = useForm<PerformanceFormValues>({
    resolver: zodResolver(performanceFormSchema),
    defaultValues: {
      name: performance.name,
      duration: performance.duration ?? 5,
      notes: performance.description ?? '',
      costumeName: performance.costume?.name,
      costumeDescription: performance.costume?.description,
      costumeCompany: performance.costume?.supplier, // Using supplier as costumeCompany
      costumeStyleNumber: performance.costume?.styleNumber,
      costumeColor: performance.costume?.color,
      // Sizing fields default values
      tightsSelection: performance.costume?.tightsSelection,
      shoeType: performance.costume?.shoeType,
      hairAndMakeupNotes: performance.costume?.hairAndMakeupNotes,
      // Extra fields
      propsNeeded: performance.costume?.propsNeeded ?? false,
      propsDescription: performance.costume?.propsDescription,
      // Bulk fill fields
      bulkCostumeSize: '',
      bulkTopSize: '',
      bulkBottomSize: '',
      bulkTightsSize: '',
      bulkShoeSize: '',
      // Individual student sizes
      studentSizes: Array.isArray(performance.studentIds)
        ? performance.studentIds.map((id) => {
            // Find existing sizing data for this student
            const existingSizing = allSizings.find(sizing => sizing.studentId?._id === id);
            return {
              studentId: id,
              studentName: studentsMap[id] || '', // Populate with actual student name
              costumeSize: existingSizing?.sizes.dressSize ?? undefined,
              topSize: existingSizing?.sizes.shirtSize ?? undefined,
              bottomSize: existingSizing?.sizes.pantSize ?? undefined,
              tightsSize: existingSizing?.sizes.custom?.tightsSize ?? undefined,
              shoeSize: existingSizing?.sizes.shoeSize ?? undefined,
              notes: existingSizing?.notes ?? undefined,
            };
          })
        : [],
      studentIds: Array.isArray(performance.studentIds)
        ? performance.studentIds.map((id) => ({
            label: studentsMap[id] || id, // Use student name as label, fallback to ID
            value: id,
          }))
        : [],
    },
  });

  const { mutateAsync: updatePerformance, isPending: isUpdatingPerformance } =
    useUpdatePerformanceMutation();
  const { mutateAsync: updateStudentCostumeSizing } = useUpdateStudentCostumeSizingMutation();

  // Update form values when student names are loaded
  useEffect(() => {
    if (!isLoadingStudents && Object.keys(studentsMap).length > 0) {
      // Update studentIds with actual names
      const updatedStudentIds = (performance.studentIds || []).map((id) => ({
        label: studentsMap[id] || id,
        value: id,
      }));

      // Update studentSizes with actual names and existing sizing data
      const updatedStudentSizes = (performance.studentIds || []).map((id) => {
        // Find existing sizing data for this student
        const existingSizing = allSizings.find(sizing => sizing.studentId?._id === id);
        return {
          studentId: id,
          studentName: studentsMap[id] ?? '',
          costumeSize: existingSizing?.sizes.dressSize,
          topSize: existingSizing?.sizes.shirtSize,
          bottomSize: existingSizing?.sizes.pantSize,
          tightsSize: existingSizing?.sizes.custom?.tightsSize,
          shoeSize: existingSizing?.sizes.shoeSize,
          notes: existingSizing?.notes,
        };
      });

      form.setValue('studentIds', updatedStudentIds);
      form.setValue('studentSizes', updatedStudentSizes);
    }
  }, [isLoadingStudents, studentsMap, performance.studentIds, form, allSizings]);

  const handleEdit = async (values: PerformanceFormValues) => {
    try {
      const sanitizedStudentIds = values.studentIds
        ? values.studentIds
            .map((student) => student.value)
            .filter((id): id is string => Boolean(id))
        : undefined;

      // First, update the performance itself
      await updatePerformance({
        actId,
        performanceId: performance._id,
        name: values.name,
        duration: values.duration,
        description: values.notes,
        studentIds: sanitizedStudentIds,
        costume: {
          name: values.costumeName ?? undefined,
          description: values.costumeDescription ?? undefined,
          supplier: values.costumeCompany ?? undefined, // Using costumeCompany as supplier
          styleNumber: values.costumeStyleNumber ?? undefined,
          color: values.costumeColor ?? undefined,
          tightsSelection: values.tightsSelection ?? undefined,
          shoeType: values.shoeType ?? undefined,
          hairAndMakeupNotes: values.hairAndMakeupNotes ?? undefined,
          propsNeeded: values.propsNeeded,
          propsDescription: values.propsDescription ?? undefined,
          imageUrl: performance.costume?.imageUrl, // Preserve existing image URL
        },
      });

      // Then, update individual student costume sizes if they exist in the form
      if (values.studentSizes && values.studentSizes.length > 0) {
        // Extract recitalId from the performance object
        const recitalId = performance.recitalId;
        
        // Process each student's size information
        const updatePromises = values.studentSizes.map(async (studentSize) => {
          // Only update if there's actual size data or notes
          if ((studentSize.costumeSize?.trim()) || 
              (studentSize.topSize?.trim()) || 
              (studentSize.bottomSize?.trim()) || 
              (studentSize.tightsSize?.trim()) || 
              (studentSize.shoeSize?.trim()) || 
              (studentSize.notes?.trim())) {
            
            // Build sizes object, filtering out empty values
            const sizes: {
              shirtSize?: string;
              dressSize?: string;
              shoeSize?: string;
              pantSize?: string;
              custom?: {
                tightsSize?: string;
              };
            } = {};
            if (studentSize.topSize?.trim()) {
              sizes.shirtSize = studentSize.topSize.trim();
            }
            if (studentSize.costumeSize?.trim()) {
              sizes.dressSize = studentSize.costumeSize.trim();
            }
            if (studentSize.shoeSize?.trim()) {
              sizes.shoeSize = studentSize.shoeSize.trim();
            }
            if (studentSize.bottomSize?.trim()) {
              sizes.pantSize = studentSize.bottomSize.trim();
            }
            if (studentSize.tightsSize?.trim()) {
              sizes.custom = {
                tightsSize: studentSize.tightsSize.trim(),
              };
            }
            
            try {
              return await updateStudentCostumeSizing({
                recitalId,
                performanceId: performance._id,
                studentId: studentSize.studentId,
                sizes: Object.keys(sizes).length > 0 ? sizes : undefined,
                notes: (studentSize.notes?.trim()) ? studentSize.notes.trim() : undefined,
              });
            } catch (error) {
              console.error(`Failed to update costume sizing for student ${studentSize.studentId}:`, error);
              toast.error(`Failed to update costume sizing for student ${studentSize.studentName}`);
              throw error; // Re-throw to stop the entire operation
            }
          }
        }).filter(promise => promise !== undefined); // Filter out undefined promises

        // Wait for all student costume size updates to complete
        if (updatePromises.length > 0) {
          try {
            await Promise.all(updatePromises);
          } catch (error) {
            console.error('One or more costume sizing updates failed:', error);
            throw new Error('Failed to update one or more student costume sizings');
          }
        }
      }

      toast.success('Performance updated successfully!');
      setOpen(false);
      form.reset({
        name: values.name,
        duration: values.duration,
        notes: values.notes,
        costumeName: values.costumeName,
        costumeDescription: values.costumeDescription,
        costumeCompany: values.costumeCompany,
        costumeStyleNumber: values.costumeStyleNumber,
        costumeColor: values.costumeColor,
        // Sizing fields
        tightsSelection: values.tightsSelection,
        shoeType: values.shoeType,
        hairAndMakeupNotes: values.hairAndMakeupNotes,
        // Extra fields
        propsNeeded: values.propsNeeded,
        propsDescription: values.propsDescription,
        // Bulk fill fields
        bulkCostumeSize: values.bulkCostumeSize,
        bulkTopSize: values.bulkTopSize,
        bulkBottomSize: values.bulkBottomSize,
        bulkTightsSize: values.bulkTightsSize,
        bulkShoeSize: values.bulkShoeSize,
        // Individual student sizes
        studentSizes: values.studentSizes,
        studentIds: values.studentIds,
      });
    } catch (error) {
      console.error('Failed to update performance:', error);
      toast.error('Failed to update performance. Please try again.');
    }
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        {children ?? (
          <Button variant='ghost' size='sm'>
            <Edit className='mr-2 size-4' />
            Edit Performance
          </Button>
        )}
      </SheetTrigger>
      <SheetContent className='w-[400px] overflow-y-auto sm:w-[540px] sm:max-w-full'>
        <SheetHeader>
          <SheetTitle>Edit Performance</SheetTitle>
          <div className='flex flex-wrap items-center gap-1 text-sm text-muted-foreground'>
            <span className='font-bold'>{performance.name}</span>
            <Dot />
            <div className='flex items-center gap-1'>
              <Clock className='size-4' />
              {performance.duration} min
            </div>
            <Dot />
            <div className='flex items-center gap-1'>
              <Users className='size-4' />
              {performance.studentIds.length} students
            </div>
          </div>
        </SheetHeader>
        <div className='mt-6'>
          {isLoadingStudents || isLoadingSizings ? (
            <div className='flex items-center justify-center py-8'>
              <Loader2 className='size-6 animate-spin text-muted-foreground' />
              <span className='ml-2 text-sm text-muted-foreground'>
                Loading student information and sizing data...
              </span>
            </div>
          ) : (
            <Form {...form}>
              <form
                onSubmit={form.handleSubmit(handleEdit)}
                className='space-y-4'
              >
                <Tabs defaultValue='basic-info' className='w-full'>
                  <TabsList className='grid w-full grid-cols-2'>
                    <TabsTrigger value='basic-info'>Basic Info</TabsTrigger>
                    <TabsTrigger value='costume-details'>
                      Costume Details
                    </TabsTrigger>
                  </TabsList>

                  <TabsContent value='basic-info' className='pt-4'>
                    <BasicInfoTab
                      control={form.control}
                      actId={actId}
                      performance={performance}
                    />
                  </TabsContent>

                  <TabsContent value='costume-details' className='pt-4'>
                    <CostumeDetailsTab
                      control={form.control}
                      actId={actId}
                      performance={performance}
                    />
                  </TabsContent>
                </Tabs>

                <SheetFooter className='mt-6'>
                  <SheetClose asChild>
                    <Button variant='outline' type='button'>
                      Cancel
                    </Button>
                  </SheetClose>
                  <Button type='submit' disabled={isUpdatingPerformance}>
                    {isUpdatingPerformance && (
                      <Loader2 className='animate-spin' />
                    )}
                    Update
                  </Button>
                </SheetFooter>
              </form>
            </Form>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};
